import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Navigation, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChevronRight, X } from 'lucide-react';
import { format } from 'date-fns';

// Videos
import heroVideo from '../assets/Mahi/0223 (3).mp4';
import mobileHeroVideo from '../assets/Mahi/0223 (3).mp4';
import heroPoster from '../assets/Mahi/44.jpeg';
import experienceVideo from '../assets/Mahi/0223 (4).mp4';

// Recommendations images - Ayurveda Resort experiences
import panchakarmaImage from '../assets/Mahi/8.jpeg';
import yogaImage from '../assets/Mahi/105.jpeg';
import cuisineImage from '../assets/Mahi/87.jpeg';
import wellnessImage from '../assets/Mahi/70.jpeg';
import spaImage from '../assets/Mahi/123.jpeg';

// Images reused for experiences/highlights
import goldImage from '../assets/Mahi/43.jpeg';
import silverImage from '../assets/Mahi/9.jpeg';
import preciousImage from '../assets/Mahi/64.jpeg';
import semiPreciousImage from '../assets/Mahi/96.jpeg';

// Gallery Images
import gallery1 from '../assets/Mahi/32.jpeg';
import gallery2 from '../assets/Mahi/40.jpeg';
import gallery3 from '../assets/Mahi/67.jpeg';
import gallery4 from '../assets/Mahi/62.jpeg';
import gallery5 from '../assets/Mahi/88.jpeg';
import gallery6 from '../assets/Mahi/93.jpeg';
import gallery7 from '../assets/Mahi/66.jpeg';
import gallery8 from '../assets/Mahi/126.jpeg';
import gallery9 from '../assets/Mahi/97.jpeg';
import gallery10 from '../assets/Mahi/68.jpeg';
import gallery11 from '../assets/Mahi/61.jpeg';
import gallery12 from '../assets/Mahi/20.jpeg';

// Resort section images
import kandy1 from '../assets/Mahi/125.jpeg';
import kandy2 from '../assets/Mahi/114.jpeg';
import kandy3 from '../assets/Mahi/28.jpeg';
import kandy4 from '../assets/Mahi/60.jpeg';
import kandy5 from '../assets/Mahi/91.jpeg';

import room1 from '../assets/Mahi/50.jpeg';
import room2 from '../assets/Mahi/37.jpeg';
import room3 from '../assets/Resort/84.jpeg';

import roomDetail from '../assets/Mahi/50.jpeg';

import poolArea from '../assets/Mahi/115.jpeg';

// Guest Departure Ceremony images
import departureImage1 from '../assets/Mahi/2.jpeg';
import departureImage2 from '../assets/Mahi/59.jpeg';
import departureImage3 from '../assets/Mahi/60.jpeg';

const sectionVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
};



// Animation variants
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const slideLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};



const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Home = () => {
    const { t } = useTranslation('home');
    const [activeIndex, setActiveIndex] = useState(null);
    const [showBookingBar, setShowBookingBar] = useState(false);
    const [hotelSearchPath, setHotelSearchPath] = useState('/search');
    const [videoSrc, setVideoSrc] = useState(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [isMobileVideo, setIsMobileVideo] = useState(false);
    const today = new Date();
    const minimumCheckout = new Date();
    minimumCheckout.setDate(today.getDate() + 3);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const isMobile = window.matchMedia('(max-width: 640px)').matches;
        const selectedSource = isMobile ? mobileHeroVideo : heroVideo;

        const timer = setTimeout(() => {
            setVideoSrc(selectedSource);
            setVideoLoaded(true);
            setIsMobileVideo(isMobile);
        }, 250);

        return () => clearTimeout(timer);
    }, []);

    const [checkInDate, setCheckInDate] = useState(formatDate(today));
    const [checkOutDate, setCheckOutDate] = useState(formatDate(minimumCheckout));

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        // Small delay looks more premium than appearing instantly
        const timer = setTimeout(() => {
            setShowBookingBar(true);
        }, 800); // 0.8 seconds after mount

        return () => clearTimeout(timer);
    }, []);

    // Optional: close button logic
    const handleClose = () => {
        setShowBookingBar(false);
    };
    const recommendations = [
        { label: t('recommendations.panchakarma.label'), name: t('recommendations.panchakarma.name'), image: panchakarmaImage },
        { label: t('recommendations.yoga.label'), name: t('recommendations.yoga.name'), image: yogaImage },
        { label: t('recommendations.cuisine.label'), name: t('recommendations.cuisine.name'), image: cuisineImage },
        { label: t('recommendations.wellness.label'), name: t('recommendations.wellness.name'), image: wellnessImage },
        { label: t('recommendations.spa.label'), name: t('recommendations.spa.name'), image: spaImage },
    ];

    const handleCheckInChange = (event) => {
        const newCheckIn = new Date(event.target.value);
        const currentCheckOut = new Date(checkOutDate);
        const minCheckOut = new Date(newCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(event.target.value);

        if (currentCheckOut < minCheckOut) {
            setCheckOutDate(formatDate(minCheckOut));
        }
    };

    const handleCheckOutChange = (event) => {
        const newCheckOut = new Date(event.target.value);
        const currentCheckIn = new Date(checkInDate);
        const minCheckOut = new Date(currentCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        if (newCheckOut >= minCheckOut) {
            setCheckOutDate(event.target.value);
        }
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://elfsightcdn.com/platform.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => console.log('Elfsight script loaded successfully');
        script.onerror = () => console.error('Failed to load Elfsight script');

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    const getServices = (t) => [
        {
            title: t('service1Title'),
            desc: t('service1Desc')
        },
        {
            title: t('service2Title'),
            desc: t('service2Desc')
        },
        {
            title: t('service3Title'),
            desc: t('service3Desc')
        },
        {
            title: t('service4Title'),
            desc: t('service4Desc')
        },
        {
            title: t('service5Title'),
            desc: t('service5Desc')
        }
    ];



    const getExperiences = (t) => [
        {
            img: goldImage,
            title: t('experience1Title'),
            desc: t('experience1Desc'),
            link: '/rooms'
        },
        {
            img: silverImage,
            title: t('experience2Title'),
            desc: t('experience2Desc'),
            link: '/ayurveda'
        },
        {
            img: preciousImage,
            title: t('experience3Title'),
            desc: t('experience3Desc'),
            link: '/food'
        },
        {
            img: semiPreciousImage,
            title: t('experience4Title'),
            desc: t('experience4Desc'),
            link: '/tours'
        },
    ];

    const getRooms = (t) => [
        {
            title: t('room1Title'),
            desc: t('room1Desc'),
            image: room1,
            link: '/rooms/luxury-suite'
        },
        {
            title: t('room2Title'),
            desc: t('room2Desc'),
            image: room2,
            link: '/rooms/deluxe'
        },
        {
            title: t('room3Title'),
            desc: t('room3Desc'),
            image: room3,
            link: '/rooms/deluxe-sea-view-room'
        },
    ];

    const services = getServices(t);

    const experiences = getExperiences(t);
    const rooms = getRooms(t);

    const getGalleryImages = (t) => [
        { src: gallery1, alt: t('gallery1Alt') },
        { src: gallery2, alt: t('gallery2Alt') },
        { src: gallery3, alt: t('gallery3Alt') },
        { src: gallery4, alt: t('gallery4Alt') },
        { src: gallery5, alt: t('gallery5Alt') },
        { src: gallery6, alt: t('gallery6Alt') },
        { src: gallery7, alt: t('gallery7Alt') },
        { src: gallery8, alt: t('gallery8Alt') },
        { src: gallery9, alt: t('gallery9Alt') },
        { src: gallery10, alt: t('gallery10Alt') },
        { src: gallery11, alt: t('gallery11Alt') },
        { src: gallery12, alt: t('gallery12Alt') },
    ];

    const getResortImages = (t) => [
        { src: kandy1, alt: t('resort1Alt') },
        { src: kandy2, alt: t('resort2Alt') },
        { src: kandy3, alt: t('resort3Alt') },
        { src: kandy4, alt: t('resort4Alt') },
        { src: kandy5, alt: t('resort5Alt') },
    ];

    const galleryImages = getGalleryImages(t);
    const resortImages = getResortImages(t);

    const kandySliderSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,

        customPaging: () => (
            <div className="w-3 h-3 rounded-full bg-white hover:bg-[#E4AB58] transition"></div>
        ),
    };

    const mobileSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,

        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
        prevArrow: (
            <div className="slick-prev !left-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#E4AB58] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                </svg>
            </div>
        ),
        nextArrow: (
            <div className="slick-next !right-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#E4AB58] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        ),
    };

    const tabSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: false,

        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
    };

    const galleryMobileSettings = {
        dots: false,
        infinite: true,
        speed: 5000,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        autoplay: true,
        autoplaySpeed: 0,
        cssEase: 'linear',
        pauseOnHover: false,
        pauseOnFocus: false,

        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
    };

    return (
        <div className="min-h-screen bg-[#E0DAC0] text-[#0D4E4A]">
            {/* Hero Section */}
            <div className="relative h-screen w-full overflow-hidden">
                {/* Video Background */}
                <div className="absolute inset-0 h-full w-full overflow-hidden">
                    <img
                        src={heroPoster}
                        alt="Mahi Beach Ayurveda Resort"
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="eager"
                    />
                    {videoLoaded && videoSrc && (
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload={isMobileVideo ? 'none' : 'metadata'}
                            poster={heroPoster}
                            className="absolute inset-0 h-full w-full object-cover"
                        >
                            <source src={videoSrc} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )}
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/70" />

                {/* Booking Bar – elegant glassmorphism style */}
                <AnimatePresence>
                    {showBookingBar && (
                        <motion.div
                            initial={{ y: -140, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -140, opacity: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            className="absolute top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-xl border-b border-[#E8C07D]/30 shadow-2xl"
                        >
                            <div className="container mx-auto px-5 sm:px-8 lg:px-12 py-5 lg:py-6">
                                <div className="relative flex flex-col gap-5 md:flex-row md:items-end md:gap-6">
                                    {/* Close Button */}
                                    <button
                                        onClick={handleClose}
                                        className="absolute -top-2 right-2 text-white/70 hover:text-[#E8C07D] text-2xl transition-colors lg:-top-3 lg:right-3"
                                        aria-label="Close booking bar"
                                    >
                                        <X size={28} />
                                    </button>

                                    {/* Hotel (single resort – but kept selector for future) */}
                                    <div className="flex-1 min-w-[220px]">
                                        <label className="block text-xs uppercase tracking-wider text-white/80 font-medium mb-1.5">
                                            {t('bookingResort')}
                                        </label>
                                        <select
                                            value={hotelSearchPath}
                                            onChange={(e) => setHotelSearchPath(e.target.value)}
                                            className="w-full bg-white/10 border border-[#E8C07D]/30 rounded-lg px-4 py-3.5 text-white placeholder-white/50 focus:outline-none focus:border-[#E8C07D] transition text-sm backdrop-blur-sm"
                                        >
                                            <option value="/search">{t('bookingResortName')}</option>
                                            {/* If you add River Resort later, keep this line */}
                                            {/* <option value="/river/search">Mahi River Ayurveda Resort</option> */}
                                        </select>
                                    </div>

                                    {/* Check-in */}
                                    <div className="flex-1 min-w-[180px]">
                                        <label className="block text-xs uppercase tracking-wider text-white/80 font-medium mb-1.5">
                                            {t('bookingCheckIn')}
                                        </label>
                                        <input
                                            type="date"
                                            value={checkInDate}
                                            min={format(new Date(), 'yyyy-MM-dd')}
                                            onChange={handleCheckInChange}
                                            className="w-full bg-white/10 border border-[#E8C07D]/30 rounded-lg px-4 py-3.5 text-white focus:outline-none focus:border-[#E8C07D] transition text-sm backdrop-blur-sm"
                                        />
                                    </div>

                                    {/* Check-out */}
                                    <div className="flex-1 min-w-[180px]">
                                        <label className="block text-xs uppercase tracking-wider text-white/80 font-medium mb-1.5">
                                            {t('bookingCheckOut')}
                                        </label>
                                        <input
                                            type="date"
                                            value={checkOutDate}
                                            min={(() => {
                                                const d = new Date(checkInDate);
                                                d.setDate(d.getDate() + 3);
                                                return format(d, 'yyyy-MM-dd');
                                            })()}
                                            onChange={handleCheckOutChange}
                                            className="w-full bg-white/10 border border-[#E8C07D]/30 rounded-lg px-4 py-3.5 text-white focus:outline-none focus:border-[#E8C07D] transition text-sm backdrop-blur-sm"
                                        />
                                    </div>

                                    {/* Guests (simple – since only Deluxe Room) */}
                                    <div className="flex-1 min-w-[160px]">
                                        <label className="block text-xs uppercase tracking-wider text-white/80 font-medium mb-1.5">
                                            {t('bookingGuests')}
                                        </label>
                                        <select className="w-full bg-white/10 border border-[#E8C07D]/30 rounded-lg px-4 py-3.5 text-white focus:outline-none focus:border-[#E8C07D] transition text-sm backdrop-blur-sm">
                                            <option>{t('booking1Guest')}</option>
                                            <option>{t('booking2Guests')}</option>
                                            <option>{t('booking3Guests')}</option>
                                        </select>
                                    </div>

                                    {/* Search Button */}
                                    <div className="flex-1 min-w-[180px] md:min-w-[200px] lg:min-w-[220px]">
                                        <div className="h-5 hidden md:block"></div>
                                        <Link
                                            to={{
                                                pathname: hotelSearchPath,
                                                search: `?checkIn=${checkInDate}&checkOut=${checkOutDate}`,
                                            }}
                                            className="w-full bg-[#E8C07D] hover:bg-white text-[#1A2F2A] hover:text-[#1A2F2A] font-semibold py-3.5 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl text-base uppercase tracking-wider text-center block"
                                        >
                                            {t('bookingCheckAvailability')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Hero Content */}
                <div className="relative z-10 container mx-auto px-5 sm:px-8 lg:px-12 h-full flex items-center justify-center">
                    <div className="text-center max-w-5xl">
                        <motion.h1
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-light text-white leading-tight drop-shadow-2xl mb-6 md:mb-8"
                        >
                            {t('heroTitle')}
                        </motion.h1>

                        <motion.div
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-xl sm:text-2xl md:text-3xl font-light text-[#E8C07D] tracking-wide mb-10 md:mb-14"
                        >
                            <span>{t('heroSubtitle1')}</span>
                            <span className="text-white/90"> {t('heroSubtitle2')}</span>
                        </motion.div>

                        <motion.div
                            variants={slideLeft}
                            initial="hidden"
                            animate="visible"
                            className="flex flex-col sm:flex-row items-center justify-center gap-5 md:gap-8"
                        >
                            <Link
                                to="/rooms"
                                className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-5 rounded-full font-medium text-lg hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all duration-400 shadow-lg hover:shadow-xl min-w-[220px]"
                            >
                                {t('heroExploreButton')}
                                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>

                            <Link
                                to="/search"
                                className="group inline-flex items-center justify-center gap-3 bg-[#E8C07D] text-[#1A2F2A] px-10 py-5 rounded-full font-semibold text-lg hover:bg-white transition-all duration-400 shadow-2xl hover:shadow-3xl min-w-[220px]"
                            >
                                {t('heroBookButton')}
                                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </motion.div>
                    </div>
                </div>

                {/* Optional elegant bottom fade */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
            </div>

            {/* open section */}
            <section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36"
            >
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <div className="flex w-full flex-col items-center">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={sectionVariants}
                            className="text-center mb-16 md:mb-20"
                        >
                            <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                {t('sections.recommendations.title')}
                            </span>
                            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                                {t('sections.recommendations.subtitle')}
                            </h2>
                            <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                        </motion.div>

                        <div className="relative mt-12 w-full lg:mt-16">
                            <Swiper
                                modules={[Navigation, Autoplay]}
                                spaceBetween={20}
                                slidesPerView={1.2}
                                centeredSlides={false}
                                loop={true}
                                autoplay={{ delay: 4000, disableOnInteraction: false }}
                                navigation={{ prevEl: '.swiper-rec-prev', nextEl: '.swiper-rec-next' }}
                                breakpoints={{
                                    640: { slidesPerView: 2.2 },
                                    768: { slidesPerView: 2.5 },
                                    1024: { slidesPerView: 3 },
                                    1280: { slidesPerView: 3.5 },
                                }}
                                className="pb-8"
                            >
                                {recommendations.map((rec) => (
                                    <SwiperSlide key={rec.name}>
                                        <motion.div
                                            variants={sectionVariants}
                                            className="group relative h-[420px] w-full overflow-hidden rounded-2xl transition-transform duration-300 hover:shadow-2xl md:h-[400px] lg:h-[480px] xl:h-[560px] cursor-pointer"
                                        >
                                            <img src={rec.image} alt={rec.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                            <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center px-4 lg:bottom-8">
                                                <h4 className="font-serif font-light text-3xl text-[#E8C07D] drop-shadow-lg md:text-2xl lg:text-3xl xl:text-3xl uppercase tracking-widest">{rec.label}</h4>
                                                <h5 className="mt-2 font-serif font-light text-xl text-white drop-shadow-md md:text-lg lg:mt-3 lg:text-2xl">{rec.name}</h5>
                                            </div>
                                        </motion.div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div className="swiper-rec-prev !left-2 top-1/2 -translate-y-1/2 !text-[#E8C07D] after:!text-3xl cursor-pointer md:!left-4 lg:after:!text-4xl"></div>
                            <div className="swiper-rec-next !right-2 top-1/2 -translate-y-1/2 !text-[#E8C07D] after:!text-3xl cursor-pointer md:!right-4 lg:after:!text-4xl"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TripAdvisor Reviews Section */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={textVariants}
                className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36"
            >
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <div className="text-center mb-12 md:mb-16">
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                            {t('tripadvisorLabel')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('tripadvisorTitle')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </div>

                    <div className="elfsight-app-15697fe9-ca38-4cbf-9e29-e68023682228" data-elfsight-app-lazy></div>
                </div>
            </motion.section>

            {/* Welcome Section (Mobile only) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-16 lg:py-24 block sm:hidden">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex flex-col items-center gap-8"
                    >
                        <div className="text-center">
                            <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                                {t('welcomeLabel')}
                            </span>
                            <h2 className="mt-4 text-4xl md:text-5xl font-serif font-light text-[#1A2F2A]">
                                {t('welcomeTitle')}
                            </h2>
                            <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                        </div>

                        <div className="relative h-[350px] w-full rounded-2xl overflow-hidden shadow-2xl group">
                            <img
                                src={kandy2}
                                alt={t('welcomeImageAlt')}
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/600x600?text=Muthumuni+Resort'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                <p className="text-sm font-semibold uppercase tracking-wider">{t('welcomeImageCaption')}</p>
                                <p className="text-xs text-white/70 mt-1">{t('welcomeImageLocation')}</p>
                            </div>
                        </div>

                        <div className="space-y-4 text-center">
                            <p className="text-lg text-[#1A2F2A]/80 leading-relaxed font-light">
                                {t('welcomeDesc1')}
                            </p>
                            <p className="text-base text-[#1A2F2A]/70 leading-relaxed font-light">
                                {t('welcomeDesc2')}
                            </p>
                        </div>

                        <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center w-full">
                            <a
                                href="/about-us"
                                className="inline-flex items-center justify-center gap-2 bg-[#1A2F2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg"
                            >
                                {t('learnMore')}
                                <span>→</span>
                            </a>
                            <a
                                href="/contact-us"
                                className="inline-flex items-center justify-center gap-2 border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-8 py-4 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                            >
                                {t('contactUs')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Welcome Section (Desktop / Tablet) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36 hidden sm:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                    >
                        {/* Content */}
                        <motion.div variants={textVariants} className="space-y-8">
                            <div>
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('welcomeLabel')}
                                </span>
                                <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                                    {t('welcomeTitle')}
                                </h2>
                            </div>

                            <div className="space-y-6 text-[#1A2F2A]/80">
                                <p className="text-lg md:text-xl leading-relaxed font-light">
                                    {t('welcomeDesc1')}
                                </p>
                                <p className="text-base md:text-lg leading-relaxed text-[#1A2F2A]/70 font-light">
                                    {t('welcomeDesc2')}
                                </p>
                            </div>

                            <div className="pt-6 flex flex-col sm:flex-row gap-5">
                                <a
                                    href="/about-us"
                                    className="inline-flex items-center justify-center gap-2 bg-[#1A2F2A] text-white px-10 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg"
                                >
                                    {t('learnMore')}
                                    <span>→</span>
                                </a>
                                <a
                                    href="/contact-us"
                                    className="inline-flex items-center justify-center gap-2 border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-10 py-4 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                                >
                                    {t('contactUs')}
                                </a>
                            </div>
                        </motion.div>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="relative"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[5/4] group">
                                <img
                                    src={kandy2}
                                    alt={t('welcomeImageAlt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/600x600?text=Muthumuni+Resort'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                            </div>

                            {/* Floating card */}
                            <div className="absolute -bottom-10 -right-6 sm:-right-12 hidden sm:block w-48 sm:w-64 aspect-square rounded-2xl overflow-hidden shadow-2xl border-8 border-white/90 rotate-6 hover:rotate-0 transition-transform">
                                <img
                                    src={kandy1}
                                    alt="Welcome detail"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Resort Highlight Section (Mobile only) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-16 block sm:hidden">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="text-center mb-12">
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                            {t('highlightTitle')}
                        </span>
                        <h2 className="mt-4 text-3xl md:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('highlightDesc')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="my-8">
                        <Slider {...mobileSettings}>
                            {resortImages.map((image, index) => (
                                <div key={index} className="px-2">
                                    <div className="relative h-[300px] w-full overflow-hidden">
                                        <img src={image.src} alt={image.alt} className="h-full w-full object-cover transition-transform duration-700 hover:scale-110" />
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <div className="space-y-4 mt-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="cursor-pointer rounded-lg border border-[#E8C07D]/30 bg-white/50 p-4 transition hover:border-[#E8C07D] hover:bg-white/70"
                                onClick={() => toggleService(index)}
                                role="button"
                                aria-expanded={activeIndex === index}
                                aria-controls={`service-${index}`}
                            >
                                <h2 className="flex items-center justify-between text-[16px] font-serif font-light text-[#1A2F2A]">
                                    {service.title}
                                    <span className="text-[25px] text-[#E8C07D]">{activeIndex === index ? '−' : '+'}</span>
                                </h2>
                                {activeIndex === index && (
                                    <p id={`service-${index}`} className="mt-2 text-[14px] text-[#1A2F2A]/70 font-light">
                                        {service.desc}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Resort Highlight Section (Desktop / Tablet) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36 hidden sm:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="text-center mb-16 md:mb-20">
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('highlightTitle')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('highlightDesc')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                        {/* Slider */}
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden">
                            <Slider {...kandySliderSettings}>
                                {resortImages.map((image, index) => (
                                    <div key={index} className="h-full">
                                        <img
                                            src={image.src}
                                            alt={image.alt}
                                            className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x550?text=Image+Not+Found'; }}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </motion.div>

                        {/* Services Accordion */}
                        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="space-y-4">
                            {services.map((service, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="cursor-pointer rounded-lg border border-[#E8C07D]/30 bg-white/50 p-5 transition hover:border-[#E8C07D] hover:bg-white/70"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-[16px] font-serif font-light text-[#1A2F2A] lg:text-[18px]">
                                        {service.title}
                                        <span className="text-[25px] text-[#E8C07D]">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <p id={`service-${index}`} className="mt-3 text-[14px] text-[#1A2F2A]/70 font-light lg:text-[16px] leading-relaxed">
                                            {service.desc}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Rooms Section (sm only) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-16 sm:py-20 block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-10"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                            {t('roomsTitle')}
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('roomsDesc')}
                        </h2>
                        <div className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="space-y-6"
                    >
                        <div className="text-center">
                            <span className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                                {t('featuredRoomLabel')}
                            </span>
                            <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                                {t('featuredRoomTitle')}
                            </h2>
                        </div>

                        <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                            <img
                                src={rooms.find(r => r.title.includes('Deluxe'))?.image || rooms[0]?.image}
                                alt="Deluxe Double Room with Sea View"
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/600x450?text=Room+Image'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </div>

                        <p className="text-base sm:text-lg leading-relaxed text-[#1A2F2A]/80 font-light text-center">
                            {rooms.find(r => r.title.includes('Deluxe'))?.desc || rooms[0]?.desc}
                        </p>

                        <div className="pt-2 flex flex-col gap-4">
                            <Link
                                to={rooms.find(r => r.title.includes('Deluxe'))?.link || rooms[0]?.link}
                                className="inline-flex items-center justify-center gap-2 bg-[#1A2F2A] text-white px-8 py-3 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg group"
                            >
                                {t('viewRoom')}
                                <span className="group-hover:translate-x-1 transition-transform">→</span>
                            </Link>
                            <Link
                                to="/search"
                                className="inline-flex items-center justify-center gap-2 border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-8 py-3 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                            >
                                {t('bookableNow')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Our Rooms Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36 hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('roomsTitle')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('roomsDesc')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                    >
                        {/* Room Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="relative order-2 lg:order-1"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[5/4] group">
                                <img
                                    src={rooms.find(r => r.title.includes('Deluxe'))?.image || rooms[0]?.image}
                                    alt="Deluxe Double Room with Sea View"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/600x450?text=Room+Image'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                            </div>

                            {/* Floating detail card */}
                            <div className="absolute -bottom-10 -right-6 sm:-right-12 hidden sm:block w-48 sm:w-64 aspect-square rounded-2xl overflow-hidden shadow-2xl border-8 border-white/90 rotate-6 hover:rotate-0 transition-transform">
                                <img
                                    src={roomDetail}
                                    alt="Room detail"
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x300?text=Detail'; }}
                                />
                            </div>
                        </motion.div>

                        {/* Room Content */}
                        <motion.div variants={textVariants} className="order-1 lg:order-2 space-y-8">
                            <div>
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('featuredRoomLabel')}
                                </span>
                                <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                                    {t('featuredRoomTitle')}
                                </h2>
                            </div>

                            <p className="text-lg md:text-xl leading-relaxed text-[#1A2F2A]/80 font-light">
                                {rooms.find(r => r.title.includes('Deluxe'))?.desc || rooms[0]?.desc}
                            </p>

                            <div className="pt-6 flex flex-col sm:flex-row gap-5">
                                <Link
                                    to={rooms.find(r => r.title.includes('Deluxe'))?.link || rooms[0]?.link}
                                    className="inline-flex items-center justify-center gap-2 bg-[#1A2F2A] text-white px-10 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg group"
                                >
                                    {t('viewRoom')}
                                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                                </Link>
                                <Link
                                    to="/search"
                                    className="inline-flex items-center justify-center gap-2 border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-10 py-4 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                                >
                                    {t('bookableNow')}
                                </Link>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>



            {/* Experiences Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('experiencesTitle')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('experiencesDesc')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    {/* Desktop Grid */}
                    <div className="mt-5 hidden grid-cols-1 gap-8 lg:grid lg:grid-cols-4">
                        {experiences.map((item, index) => (
                            <motion.div
                                key={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={scaleUp}
                                className="group relative h-[350px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 bg-white/50 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl hover:bg-white/70"
                            >
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    className="h-[200px] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400x250?text=Experience'; }}
                                />
                                <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                    <h2 className="text-center text-[18px] md:text-[20px] font-serif font-light text-[#1A2F2A]">
                                        {item.title}
                                    </h2>
                                    <p className="text-center text-[14px] text-[#1A2F2A]/70 font-light">
                                        {item.desc}
                                    </p>
                                    <a
                                        href={item.link}
                                        className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#E8C07D] text-[14px] font-semibold text-[#E8C07D] hover:bg-[#E8C07D] hover:text-white transition"
                                    >
                                        →
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile & Tablet Sliders */}
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="block md:hidden">
                        <Slider {...mobileSettings}>
                            {experiences.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[450px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 bg-white/50 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl hover:bg-white/70">
                                        <img src={item.img} alt={item.title} className="h-[200px] w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        <div className="flex h-[250px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-serif font-light text-[#1A2F2A]">{item.title}</h2>
                                            <p className="text-center text-[14px] text-[#1A2F2A]/70 font-light">{item.desc}</p>
                                            <a href={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#E8C07D] text-[14px] font-semibold text-[#E8C07D] hover:bg-[#E8C07D] hover:text-white transition">
                                                →
                                            </a>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="hidden md:block lg:hidden">
                        <Slider {...tabSettings}>
                            {experiences.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[300px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 bg-white/50 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl hover:bg-white/70">
                                        <img src={item.img} alt={item.title} className="h-[150px] w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-serif font-light text-[#1A2F2A]">{item.title}</h2>
                                            <p className="text-center text-[14px] text-[#1A2F2A]/70 font-light">{item.desc}</p>
                                            <a href={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#E8C07D] text-[14px] font-semibold text-[#E8C07D] hover:bg-[#E8C07D] hover:text-white transition">
                                                →
                                            </a>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('galleryTitle')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('galleryDesc')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    {/* Desktop Grid - 3 columns with masonry layout */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="hidden lg:grid grid-cols-3 gap-6"
                    >
                        {galleryImages.map((image, index) => (
                            <motion.div
                                key={index}
                                variants={scaleUp}
                                className="group relative h-[380px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl"
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Tablet Grid - 2 columns */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="hidden md:grid lg:hidden grid-cols-2 gap-6"
                    >
                        {galleryImages.map((image, index) => (
                            <motion.div
                                key={index}
                                variants={scaleUp}
                                className="group relative h-[300px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl"
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Mobile Slider */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="block md:hidden"
                    >
                        <Slider {...galleryMobileSettings}>
                            {galleryImages.map((image, index) => (
                                <div key={index} className="px-4">
                                    <motion.div
                                        variants={scaleUp}
                                        className="group relative h-[350px] w-full overflow-hidden rounded-2xl border border-[#E8C07D]/30 shadow-lg transition hover:border-[#E8C07D] hover:shadow-2xl"
                                    >
                                        <img
                                            src={image.src}
                                            alt={image.alt}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>
                </div>
            </section>

            {/* Experience Video Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('videoLabel')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('videoTitle')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={scaleUp}
                        className="relative"
                    >
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl group h-[300px] sm:h-[400px] md:h-[500px] lg:h-[700px]">
                            <video
                                src={experienceVideo}
                                autoPlay
                                controls
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-12 md:mt-16 text-center"
                    >
                        <p className="text-lg md:text-xl leading-relaxed text-[#1A2F2A]/80 font-light max-w-3xl mx-auto">
                            {t('videoDescription')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Pool Area Section (sm only) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-16 sm:py-20 block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-10"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                            {t('poolLabel')}
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('poolTitle')}
                        </h2>
                        <div className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="space-y-6"
                    >
                        <div className="relative h-[300px] sm:h-[380px] rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src={poolArea}
                                alt={t('poolImageAlt')}
                                className="h-full w-full object-cover"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Pool+Area'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </div>

                        <div className="space-y-4 text-[#1A2F2A]/80 text-center">
                            <p className="text-base sm:text-lg leading-relaxed font-light">
                                {t('poolDesc1')}
                            </p>
                            <p className="text-sm sm:text-base leading-relaxed text-[#1A2F2A]/70 font-light">
                                {t('poolDesc2')}
                            </p>
                        </div>

                        <div className="space-y-3 pt-2">
                            {[
                                { icon: '🏊', key: 'poolFeature1' },
                                { icon: '🌴', key: 'poolFeature2' },
                                { icon: '🍹', key: 'poolFeature3' },
                                { icon: '🛋️', key: 'poolFeature4' }
                            ].map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ delay: idx * 0.1 }}
                                    variants={scaleUp}
                                    className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#E8C07D]/20 hover:border-[#E8C07D]/40"
                                >
                                    <span className="text-[22px] sm:text-[24px]">{item.icon}</span>
                                    <span className="text-[15px] sm:text-[16px] text-[#1A2F2A]/80">{t(item.key)}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Pool Area Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36 hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('poolLabel')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('poolTitle')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                    >
                        {/* Pool Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="relative order-2 lg:order-1"
                        >
                            <div className="relative h-[380px] sm:h-[460px] md:h-[520px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                <img
                                    src={poolArea}
                                    alt={t('poolImageAlt')}
                                    className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Pool+Area'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                            </div>

                            {/* Floating detail card */}
                            <div className="absolute -bottom-10 -right-6 sm:-right-12 hidden sm:block w-48 sm:w-64 aspect-square rounded-2xl overflow-hidden shadow-2xl border-8 border-white/90 rotate-6 hover:rotate-0 transition-transform">
                                <img
                                    src={poolArea}
                                    alt="Pool detail"
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x300?text=Pool+Detail'; }}
                                />
                            </div>
                        </motion.div>

                        {/* Pool Content */}
                        <motion.div variants={textVariants} className="order-1 lg:order-2 space-y-8">
                            <div className="space-y-4 text-[#1A2F2A]/80">
                                <p className="text-lg md:text-xl leading-relaxed font-light">
                                    {t('poolDesc1')}
                                </p>
                                <p className="text-base md:text-lg leading-relaxed text-[#1A2F2A]/70 font-light">
                                    {t('poolDesc2')}
                                </p>
                            </div>

                            <div className="space-y-3 pt-4">
                                {[
                                    { icon: '🏊', key: 'poolFeature1' },
                                    { icon: '🌴', key: 'poolFeature2' },
                                    { icon: '🍹', key: 'poolFeature3' },
                                    { icon: '🛋️', key: 'poolFeature4' }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{ delay: idx * 0.1 }}
                                        variants={scaleUp}
                                        className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#E8C07D]/20 hover:border-[#E8C07D]/40"
                                    >
                                        <span className="text-[22px] sm:text-[24px]">{item.icon}</span>
                                        <span className="text-[15px] sm:text-[16px] text-[#1A2F2A]/80">{t(item.key)}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Guest Departure Ceremony Section (sm only) */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-16 sm:py-20 block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-10"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                            {t('departureLabel')}
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('departureTitle')}
                        </h2>
                        <div className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="space-y-6"
                    >
                        <div className="relative h-[300px] sm:h-[380px] rounded-3xl overflow-hidden shadow-2xl">
                            <Slider
                                dots={false}
                                infinite={true}
                                speed={1000}
                                slidesToShow={1}
                                slidesToScroll={1}
                                autoplay={true}
                                autoplaySpeed={3000}
                                arrows={false}
                                rtl={true}
                                cssEase="ease-in-out"
                            >
                                {[departureImage1, departureImage2, departureImage3].map((image, index) => (
                                    <div key={index} className="h-full">
                                        <div className="relative h-[300px] sm:h-[380px] group">
                                            <img
                                                src={image}
                                                alt={`${t('departureImageAlt')} ${index + 1}`}
                                                className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </div>

                        <div className="space-y-4 text-[#1A2F2A]/80 text-center">
                            <p className="text-base sm:text-lg leading-relaxed font-light">
                                {t('departureDesc1')}
                            </p>
                            <p className="text-sm sm:text-base leading-relaxed text-[#1A2F2A]/70 font-light">
                                {t('departureDesc2')}
                            </p>
                        </div>

                        <div className="space-y-3 pt-2">
                            {[
                                { icon: '🙏', key: 'departureFeature1' },
                                { icon: '🎁', key: 'departureFeature2' },
                                { icon: '🍽️', key: 'departureFeature3' },
                                { icon: '📿', key: 'departureFeature4' }
                            ].map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ delay: idx * 0.1 }}
                                    variants={scaleUp}
                                    className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#E8C07D]/20 hover:border-[#E8C07D]/40"
                                >
                                    <span className="text-[22px] sm:text-[24px]">{item.icon}</span>
                                    <span className="text-[15px] sm:text-[16px] text-[#1A2F2A]/80">{t(item.key)}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Guest Departure Ceremony Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36 hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('departureLabel')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('departureTitle')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                    >
                        {/* Left side - Description */}
                        <motion.div variants={textVariants} className="space-y-8 order-2 lg:order-1">
                            <div className="space-y-4 text-[#1A2F2A]/80">
                                <p className="text-lg md:text-xl leading-relaxed font-light">
                                    {t('departureDesc1')}
                                </p>
                                <p className="text-base md:text-lg leading-relaxed text-[#1A2F2A]/70 font-light">
                                    {t('departureDesc2')}
                                </p>
                            </div>

                            <div className="space-y-3 pt-4">
                                {[
                                    { icon: '🙏', key: 'departureFeature1' },
                                    { icon: '🎁', key: 'departureFeature2' },
                                    { icon: '🍽️', key: 'departureFeature3' },
                                    { icon: '📿', key: 'departureFeature4' }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{ delay: idx * 0.1 }}
                                        variants={scaleUp}
                                        className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#E8C07D]/20 hover:border-[#E8C07D]/40"
                                    >
                                        <span className="text-[22px] sm:text-[24px]">{item.icon}</span>
                                        <span className="text-[15px] sm:text-[16px] text-[#1A2F2A]/80">{t(item.key)}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right side - Sliding Images */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="relative order-1 lg:order-2"
                        >
                            <div className="relative h-[380px] sm:h-[460px] md:h-[520px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl">
                                <Slider
                                    dots={false}
                                    infinite={true}
                                    speed={1000}
                                    slidesToShow={1}
                                    slidesToScroll={1}
                                    autoplay={true}
                                    autoplaySpeed={3000}
                                    arrows={false}
                                    rtl={true}
                                    cssEase="ease-in-out"
                                >
                                    {[departureImage1, departureImage2, departureImage3].map((image, index) => (
                                        <div key={index} className="h-full">
                                            <div className="relative h-[380px] sm:h-[460px] md:h-[520px] lg:h-[700px] group">
                                                <img
                                                    src={image}
                                                    alt={`${t('departureImageAlt')} ${index + 1}`}
                                                    className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                                            </div>
                                        </div>
                                    ))}
                                </Slider>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] py-20 md:py-28 lg:py-36">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('testimonialsLabel')}
                        </span>
                        <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('testimonialsTitle')}
                        </h2>
                        <div className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="py-12 md:py-16 min-h-[400px]"
                    >
                        <div className="elfsight-app-7d765cd2-1c57-481f-955c-1594cd5cddd1"></div>
                    </motion.div>
                </div>
            </section>
        </div >
    );
};

export default Home;
